import React, { useState, useEffect } from "react";
import { axiosPrivate } from "../../../api/axios";

const NOTIFICATION_TYPES = [
  { value: "info", label: "Info" },
  { value: "warning", label: "Warning" },
  { value: "update", label: "Update" },
  { value: "maintenance", label: "Maintenance" },
  { value: "announcement", label: "Announcement" },
];

const PRIORITY_LEVELS = [
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

const ACTION_TYPES = [
  { value: "", label: "None" },
  { value: "link", label: "External Link" },
  { value: "navigate", label: "Navigate (in-app)" },
];

const DesktopNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [creating, setCreating] = useState(false);

  // Form state
  const [type, setType] = useState("info");
  const [priority, setPriority] = useState("normal");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [actionType, setActionType] = useState("");
  const [actionTarget, setActionTarget] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [targetEmails, setTargetEmails] = useState("");

  const fetchNotifications = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosPrivate.post("/admin/desktop/notifications");
      setNotifications(response.data.notifications || []);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || "Failed to fetch notifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        type,
        priority,
        title,
        message,
      };

      if (actionType) {
        payload.action = { type: actionType, target: actionTarget };
      }

      if (expiresAt) {
        payload.expiresAt = new Date(expiresAt).toISOString();
      }

      if (targetEmails.trim()) {
        payload.targetEmails = targetEmails
          .split(",")
          .map((email) => email.trim())
          .filter((email) => email);
      }

      await axiosPrivate.post("/admin/desktop/notifications/create", payload);
      setSuccess("Notification created successfully");

      // Reset form
      setType("info");
      setPriority("normal");
      setTitle("");
      setMessage("");
      setActionType("");
      setActionTarget("");
      setExpiresAt("");
      setTargetEmails("");

      // Refresh list
      fetchNotifications();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || "Failed to create notification");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this notification?")) {
      return;
    }

    try {
      await axiosPrivate.post("/admin/desktop/notifications/delete", { id });
      setSuccess("Notification deleted");
      fetchNotifications();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || "Failed to delete notification");
    }
  };

  const getPriorityBadgeClass = (priority) => {
    switch (priority) {
      case "critical":
        return "bg-red-100 text-red-800";
      case "high":
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-100 border border-green-400 text-green-700 rounded">
          {success}
        </div>
      )}

      {/* Create Notification Form */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Create Notification</h3>
        <form onSubmit={handleCreate} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {NOTIFICATION_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Priority</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {PRIORITY_LEVELS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Notification title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Notification message"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Action Type</label>
              <select
                value={actionType}
                onChange={(e) => setActionType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {ACTION_TYPES.map((a) => (
                  <option key={a.value} value={a.value}>
                    {a.label}
                  </option>
                ))}
              </select>
            </div>
            {actionType && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  {actionType === "link" ? "URL" : "Route"}
                </label>
                <input
                  type="text"
                  value={actionTarget}
                  onChange={(e) => setActionTarget(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={actionType === "link" ? "https://..." : "/settings"}
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Expires At (optional)
              </label>
              <input
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Target Emails (optional)
              </label>
              <input
                type="text"
                value={targetEmails}
                onChange={(e) => setTargetEmails(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="email1@example.com, email2@example.com"
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty to send to all users
              </p>
            </div>
          </div>

          <button
            type="submit"
            disabled={creating}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-md disabled:opacity-50"
          >
            {creating ? "Creating..." : "Create Notification"}
          </button>
        </form>
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Active Notifications</h3>
        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : notifications.length === 0 ? (
          <p className="text-gray-500">No active notifications</p>
        ) : (
          <div className="space-y-4">
            {notifications.map((notif) => (
              <div
                key={notif.id}
                className="border rounded-lg p-4 flex justify-between items-start"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityBadgeClass(
                        notif.priority
                      )}`}
                    >
                      {notif.priority}
                    </span>
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                      {notif.type}
                    </span>
                  </div>
                  <h4 className="font-medium">{notif.title}</h4>
                  <p className="text-sm text-gray-600 mt-1">{notif.message}</p>
                  {notif.expiresAt && (
                    <p className="text-xs text-gray-400 mt-2">
                      Expires: {new Date(notif.expiresAt).toLocaleString()}
                    </p>
                  )}
                  {notif.targetEmails && (
                    <p className="text-xs text-gray-400">
                      Target: {notif.targetEmails.join(", ")}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => handleDelete(notif.id)}
                  className="text-red-500 hover:text-red-700 text-sm ml-4"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DesktopNotifications;
